import { type ReactNode, useEffect, useState } from "react";

import { Button, Card, CardHead, Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { TraceEnvironment } from "./conversationsSearch";
import { type RecentLookup, shortSessionId } from "./sessionLookup";

/** Why an addressed conversation did not open, in the words the reader needs. */
export type LookupNotice =
  | { kind: "invalid"; input: string }
  | { kind: "load_failed"; input: string; code: string; message: string };

/**
 * The way into a conversation besides a link: search the sessions, or paste
 * one `session_id` (fe/CONVERSATIONS.md §3.1).
 *
 * The same bar heads the list and an open conversation, so another session is
 * one search away from either. What was typed is a draft until 조회; the
 * address is the search, and back/forward — or opening a conversation — puts
 * the draft back in step with it.
 */
export function ConversationSearchBar({
  variant,
  environment,
  q,
  email,
  session,
  recents,
  onEnvironment,
  onSubmit,
  onPickRecent,
  evaluationRuntime = false,
}: {
  /** `list` also offers the recent chips and says what the screen is for. */
  variant: "list" | "detail";
  environment: TraceEnvironment;
  q: string | null;
  email: string | null;
  session: string | null;
  recents: readonly RecentLookup[];
  onEnvironment: (env: TraceEnvironment) => void;
  onSubmit: (q: string, email: string) => void;
  onPickRecent: (entry: RecentLookup) => void;
  evaluationRuntime?: boolean;
}) {
  const [qDraft, setQDraft] = useState(q ?? "");
  const [emailDraft, setEmailDraft] = useState(email ?? "");
  useEffect(() => {
    setQDraft(q ?? "");
    setEmailDraft(email ?? "");
  }, [q, email, session]);

  const form = (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(qDraft, emailDraft);
      }}
    >
      {/* Environment first: the same id means nothing in the other
          database, so it is part of the address, not a filter. */}
      {!evaluationRuntime && <div role="group" aria-label="환경" className="flex gap-1">
        {(["dev", "prod"] as const).map((env) => (
          <button
            key={env}
            type="button"
            aria-pressed={environment === env}
            onClick={() => onEnvironment(env)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition",
              environment === env
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:bg-muted/40",
            )}
          >
            {env}
          </button>
        ))}
      </div>}
      <Input
        aria-label="검색어"
        className="w-full min-w-0 sm:w-80"
        placeholder="session_id · 세션 이름 · 멤버"
        autoComplete="off"
        // On the list the bar is the first thing to use; over an open
        // conversation it must not take focus from the thread.
        autoFocus={variant === "list"}
        value={qDraft}
        onChange={(event) => setQDraft(event.target.value)}
      />
      <Input
        aria-label="멤버 이메일"
        // Not `type="email"`: its validation would block 조회 on a partial
        // address instead of letting the empty result say it matched nothing.
        inputMode="email"
        className="w-full min-w-0 sm:w-60"
        placeholder="멤버 이메일 (정확히 일치)"
        autoComplete="off"
        value={emailDraft}
        onChange={(event) => setEmailDraft(event.target.value)}
      />
      <Button type="submit" size="sm">
        조회
      </Button>
      <span className="text-xs text-muted-foreground">
        session_id 는 바로 열립니다 · 비우고 조회하면 최근 활동순 전체
      </span>
    </form>
  );

  if (variant === "detail") {
    return <Card className="px-4 py-3">{form}</Card>;
  }

  return (
    <Card>
      <CardHead title="대화 검색" hint="세션 이름 · 멤버 · session_id" />
      <div className="grid gap-3 p-4">
        {form}

        {recents.length > 0 && (
          <div className="flex items-start gap-2">
            <span className="w-20 shrink-0 pt-1 text-xs text-muted-foreground">최근 조회</span>
            <div className="flex flex-wrap gap-1.5">
              {recents.map((entry) => (
                <button
                  key={`${entry.env}:${entry.id}`}
                  type="button"
                  title={`${entry.env} · ${entry.id}`}
                  onClick={() => onPickRecent(entry)}
                  className="rounded-full border border-border bg-card px-2.5 py-0.75 text-[11px] text-muted-foreground transition hover:border-primary hover:text-foreground"
                >
                  {/* The environment is printed only when it is not the one
                      selected — otherwise every chip repeats it. */}
                  {entry.env !== environment && <span className="mr-1">{entry.env} ·</span>}
                  {shortSessionId(entry.id)}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="rounded-lg border border-border bg-surface-subtle px-3 py-2 text-xs text-muted-foreground">
          Conversations 는 👎 여부와 무관하게 모든 대화를 다룹니다. 평가·분류(TC, 도메인)는
          여기에 없습니다 — 이 화면은 실행된 대화 그 자체와 Trace 만 봅니다.
        </p>
      </div>
    </Card>
  );
}

/**
 * The conversation the address named did not open. Shown above the list, so
 * the reader is told why and can pick another in the same place.
 */
export function LookupNoticeCard({
  environment,
  notice,
}: {
  environment: TraceEnvironment;
  notice: LookupNotice;
}) {
  const input = notice.input && <span className="break-all">입력한 값: "{notice.input}"</span>;

  return (
    <Card>
      {notice.kind === "invalid" ? (
        <Notice title="session_id 형식이 아닙니다.">
          {input}
          <span>전체 UUID(8-4-4-4-12)를 넣거나, 아래 목록에서 대화를 골라 주세요.</span>
        </Notice>
      ) : (
        <LoadFailed environment={environment} notice={notice} input={input} />
      )}
    </Card>
  );
}

/**
 * The session request failed. The codes stay apart for the same reason the
 * trace's four do: each one sends the reader somewhere different.
 */
function LoadFailed({
  environment,
  notice,
  input,
}: {
  environment: TraceEnvironment;
  notice: Extract<LookupNotice, { kind: "load_failed" }>;
  input: ReactNode;
}) {
  switch (notice.code) {
    case "SESSION_NOT_FOUND": {
      const other = environment === "dev" ? "prod" : "dev";
      return (
        <Notice title="해당 session_id 로 저장된 대화가 없습니다.">
          {input}
          <span>
            {environment} DB 에서 찾았습니다 — {other} 의 대화라면 위에서 환경을 바꿔 조회하세요.
          </span>
        </Notice>
      );
    }
    case "PROD_DB_NOT_CONFIGURED":
      return (
        <Notice title="prod DB가 설정되지 않아 prod 대화를 읽을 수 없습니다.">{input}</Notice>
      );
    case "PROD_LOAD_FAILED":
      return (
        <Notice title="prod DB에서 대화를 읽지 못했습니다.">
          {input}
          <span className="break-all">{notice.message}</span>
        </Notice>
      );
    default:
      return (
        <Notice title="대화를 불러오지 못했습니다.">
          {input}
          <span className="break-all">{notice.message}</span>
        </Notice>
      );
  }
}

function Notice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div
      role="alert"
      className="grid justify-items-center gap-1.5 px-4 py-6 text-center text-[13px] text-muted-foreground"
    >
      <p className="text-sm text-destructive">{title}</p>
      {children}
    </div>
  );
}
